import { RuleEngineApiDto } from '@app/pages/checkout-app/checkout-layout/main/components/ab-testing/react/models/AbTestRulesApiDto';
import { Operator, Property } from '../models/RuleEngineConfig';
import { RuleEngineState } from '../models/RuleEngineState';
import { getKeyByStringValue } from '@utils/string-utils';
import {
  CountryCode,
  CountryCodeOptions,
  CountryRegionType,
  StateCode,
  countryNameToCountryCodeMap,
  getCountryCodeByCountryDisplayName,
  getCountryDisplayNameByCountryCode,
} from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { Rule } from '../models/Rule';
import { PROPERTY_OPERATOR_MAPPING, PropertyOperatorMapping, TimeFrame } from '../mapping/PropertyOperatorMapping';
import { ValueDropdownOptions } from '../mapping/ValueDropdownOptions';
import { RuleGroup } from '../models/RuleGroup';

export const mapRuleEngineApiDtoToRuleEngineState = (abTestRulesApiDto: RuleEngineApiDto): RuleEngineState => {
  const ruleGroups = abTestRulesApiDto?.ruleGroups?.map((ruleGroupApiDto, groupIndex) => {
    const rules = ruleGroupApiDto?.rules?.map((ruleApiDto, rulesIndex) => {
      return {
        internalId: rulesIndex,
        property: Property[ruleApiDto.type],
        operator: Operator[ruleApiDto.operator],
        value: ruleApiDto.value,
        measuringUnit: ruleApiDto.measuringUnit,
        optionalValue: ruleApiDto.optionalValue,
      };
    });
    return {
      internalId: groupIndex,
      matchAllRules: rules?.length == 1 ? true : ruleGroupApiDto.matchAllRules,
      rules,
      apiId: null,
      addedFromPredictedSegment: ruleGroupApiDto.addedFromPredictedSegment,
    };
  });
  return {
    ruleGroups,
    matchAllRuleGroups: abTestRulesApiDto.matchAllRuleGroups,
  };
};

export const mapRuleEngineStateToRuleEngineApiDto = (ruleEngineState: RuleEngineState): RuleEngineApiDto => {
  const ruleGroups = ruleEngineState.ruleGroups.map((ruleGroup) => {
    const rules = ruleGroup.rules.map((rule) => {
      return {
        type: getKeyByStringValue(Property, rule.property),
        operator: getKeyByStringValue(Operator, rule.operator),
        value: rule.value,
        measuringUnit: rule.measuringUnit,
        optionalValue: rule.optionalValue,
      };
    });
    return {
      matchAllRules: rules?.length == 1 ? true : ruleGroup.matchAllRules,
      rules,
    };
  });
  return {
    matchAllRuleGroups: ruleEngineState.matchAllRuleGroups,
    ruleGroups,
  };
};

export const mapCheckoutSegmentToRuleEngineState = (segmentedRuleGroup: RuleGroup): RuleEngineState => {
  const rules = segmentedRuleGroup?.rules?.map((rule, rulesIndex) => {
    return {
      internalId: rulesIndex,
      property: rule.property,
      operator: rule.operator,
      value: rule.value,
      measuringUnit: rule.measuringUnit,
      optionalValue: rule.optionalValue,
    };
  });
  const ruleGroup = {
    internalId: 0,
    matchAllRules: true,
    rules,
    apiId: null,
    addedFromPredictedSegment: true,
  };
  return {
    ruleGroups: [ruleGroup],
    matchAllRuleGroups: true,
  };
};

/**
 * Maps checkout segment rule groups (with API DTO format: type/operator as strings)
 * to RuleEngineState format. Handles conversion of type -> Property and operator -> Operator enums.
 */
export const mapCheckoutSegmentRuleGroupsToRuleEngineState = (
  ruleGroups: Array<{
    matchAllRules?: boolean;
    rules?: Array<{
      type?: string;
      operator?: string;
      property?: Property;
      value?: string;
      measuringUnit?: string;
      optionalValue?: string;
    }>;
  }>,
): RuleEngineState => {
  let groupInternalIdCounter = 0;
  const mappedRuleGroups = ruleGroups.map((ruleGroup) => {
    const rules =
      ruleGroup.rules?.map((rule, rulesIndex) => {
        // Handle both API DTO format (type/operator as strings) and already-mapped format (property/operator as enums)
        const ruleApiDto = rule as any;
        return {
          internalId: rulesIndex,
          property: Property[ruleApiDto.type] || rule.property,
          operator: Operator[ruleApiDto.operator] || rule.operator,
          value: rule.value,
          measuringUnit: rule.measuringUnit,
          optionalValue: rule.optionalValue,
        };
      }) || [];

    return {
      internalId: groupInternalIdCounter++,
      matchAllRules: rules.length === 1 ? true : (ruleGroup.matchAllRules ?? true),
      rules,
      apiId: null,
      addedFromPredictedSegment: false,
    };
  });

  return {
    matchAllRuleGroups: true,
    ruleGroups: mappedRuleGroups,
  };
};

export function mapCountriesAndRegionsValue(value: string | null | undefined): {
  statesByCountry: Partial<Record<CountryCodeOptions, StateCode[]>>;
} {
  if (!value) return { statesByCountry: {} };

  try {
    const obj = JSON.parse(value) as Record<string, StateCode[]>;
    const statesByCountry = obj as Partial<Record<CountryCodeOptions, StateCode[]>>;
    return { statesByCountry };
  } catch {}

  const code =
    countryNameToCountryCodeMap[value.trim().toLowerCase()] ??
    (getCountryCodeByCountryDisplayName(value) as CountryCodeOptions);

  return { statesByCountry: code ? { [code]: [] } : {} };
}

export const mapRuleEngineStateMeasurementUnitsAndOptionalValues = (rule: Rule): string => {
  const mapping = PROPERTY_OPERATOR_MAPPING.find(
    (mapping) => mapping.property === rule.property,
  ) as PropertyOperatorMapping;
  if (rule.property === Property.customerOrderCount) {
    const match = mapping.dropdownOptions.find((opt) => opt.value === rule.measuringUnit);
    const unitLabel = match ? match.label : rule.measuringUnit;
    if (!unitLabel) {
      return 'all time';
    }
    let unitValue = '';
    if (rule.measuringUnit === TimeFrame.WITHIN_LAST_DAYS) {
      unitValue = ` ${rule.optionalValue} Calender days`;
    }
    return `${unitLabel}${unitValue}`;
  } else if (rule.property === Property.grossMargin) {
    const match = mapping.dropdownOptions.find((opt) => opt.value === rule.measuringUnit);
    const unitLabel = match ? match.label : rule.measuringUnit;
    return `${unitLabel}`;
  }

  let text = '';
  if (rule.measuringUnit) {
    text += ` ${rule.measuringUnit}`;
  }
  if (rule.optionalValue) {
    text += ` ${rule.optionalValue}`;
  }
  return text;
};

export function getAbTestRuleAsDescription(rule: Rule | null | undefined) {
  let text = '';
  if (
    rule.property.toLowerCase() === 'countries' ||
    rule.property.toLowerCase() === 'country' ||
    rule.property.toLowerCase() === 'destinations'
  ) {
    text = getCountryRuleText(rule);
  } else if (rule.property.toLowerCase() === 'weekday') {
    text = getWeekdaysRuleText(rule);
  } else {
    text = getRuleText(rule);
  }
  return text;
}

export function getCountryRuleTextDescription(rule: Rule | null | undefined) {
  return getCountryRuleText(rule, true);
}

const MAX_VISIBLE_COUNTRIES = 6;

const getCountryRuleText = (rule: Rule, skipMaxCountriesVisibleCheck: boolean = false): string => {
  const rawValue = rule.value;
  let parsed: Record<string, string[]>;

  try {
    parsed = JSON.parse(rawValue);
  } catch {
    return `${rule.property} ${rule.operator} ${rawValue}`;
  }

  const parts: string[] = Object.entries(parsed).map(([countryCode, stateArray]) => {
    const countryName = getCountryDisplayNameByCountryCode(countryCode);
    if (!countryName) {
      return countryCode;
    }

    if (Array.isArray(stateArray) && stateArray.length > 0) {
      const subdivisionLabel = countryCode === CountryCode.CA ? CountryRegionType.PROVINCES : CountryRegionType.STATES;
      return `${countryName} (${stateArray.length} ${subdivisionLabel})`;
    }

    return countryName;
  });

  let value: string;
  if (!skipMaxCountriesVisibleCheck && parts.length > MAX_VISIBLE_COUNTRIES) {
    const visible = parts.slice(0, MAX_VISIBLE_COUNTRIES);
    const remainingCount = parts.length - MAX_VISIBLE_COUNTRIES;
    visible.push(`+${remainingCount} More`);
    value = visible.join(', ');
  } else {
    if (parts.length > 1) {
      const allExceptLast = parts.slice(0, -1);
      const last = parts[parts.length - 1];
      value = `${allExceptLast.join(', ')} and ${last}`;
    } else {
      value = parts.join(', ');
    }
  }
  return skipMaxCountriesVisibleCheck ? `${value}` : `${rule.property} ${rule.operator} ${value}`;
};

const getWeekdaysRuleText = (rule: Rule): string => {
  const rawValue = rule.value;
  let parsed: Record<string, string[]>;

  try {
    parsed = JSON.parse(rawValue);
  } catch {
    // If JSON.parse fails, just fall back to the raw string:
    return rawValue;
  }
  return `Weekdays is ${(parsed as any).join(', ')}`;
};

const getRuleText = (rule: Rule): string => {
  const dropdownValueOptions = ValueDropdownOptions[rule.property];
  let value = rule.value;
  if (dropdownValueOptions) {
    const option = dropdownValueOptions.find((opt) => opt.value === value);
    if (option) {
      value = option.label;
    }
  }
  let text = `${rule.property} ${rule.operator} ${value}`;
  text += ` ${mapRuleEngineStateMeasurementUnitsAndOptionalValues(rule)}`;
  return text;
};
